/**
 * The interface TYPES a program builds its interfaces from (issue #52).
 *
 * The library's three disciplines first, shown and not editable - they are
 * shared with every program - then the program's own, each with the disciplines
 * it reaches, what it is built on, the attributes it adds, and what uses it. A
 * type something is built on cannot be deleted, and the button says what uses it
 * rather than failing after the click.
 */

import { useCallback, useEffect, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { InterfaceTypeRow } from '../../schema'
import { useCanWrite } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import ConfirmDialog from '../common/ConfirmDialog'
import CreateInterfaceTypeDialog from './CreateInterfaceTypeDialog'

export default function InterfaceTypesPanel({ onChanged = () => {} }: { onChanged?: () => void }) {
  const permission = useCanWrite()
  const toasts = useToasts()
  const [types, setTypes] = useState<InterfaceTypeRow[] | null>(null)
  const [failure, setFailure] = useState('')
  const [creating, setCreating] = useState(false)
  const [removing, setRemoving] = useState<InterfaceTypeRow | null>(null)
  const [reloads, setReloads] = useState(0)

  useEffect(() => {
    let live = true
    api.interfaceTypes()
      .then((found) => live && setTypes(found))
      .catch((error: ApiError) => live && setFailure(error.message))
    return () => {
      live = false
    }
  }, [reloads])

  const changed = useCallback(() => {
    setReloads((count) => count + 1)
    onChanged()
  }, [onChanged])

  const remove = async (type: InterfaceTypeRow) => {
    try {
      await api.deleteInterfaceType(type.qualified_name)
      toasts.succeeded(`${type.name} removed`)
      changed()
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setRemoving(null)
    }
  }

  // The disciplines and the program's own. MessageInterface and
  // PhysicalInterface - library plumbing that reaches no discipline - are left
  // out: nothing here is built on them directly any more.
  const shown = (types ?? []).filter((type) => type.disciplines.length > 0)

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, maxWidth: '80ch' }}>
          A type says what KIND of interface something is - which half of the ICD it belongs in,
          and what it always states. Build your own on the library&apos;s disciplines, combining
          them where an interface is more than one: software over a standard connector is both.
        </Typography>
        <Tooltip title={permission.allowed ? '' : permission.reason} describeChild>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreating(true)}
              disabled={!permission.allowed || !types}
            >
              New interface type
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {failure && <Alert severity="error" onClose={() => setFailure('')}>{failure}</Alert>}

      <Table size="small" aria-label="Interface types">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Discipline</TableCell>
            <TableCell>Built on</TableCell>
            <TableCell>Adds</TableCell>
            <TableCell>Used by</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((type) => (
            <TableRow key={type.qualified_name}>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>{type.name}</Typography>
                {type.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: '50ch' }}>
                    {type.description}
                  </Typography>
                )}
                {type.in_library && <Chip size="small" variant="outlined" label="library" sx={{ mt: 0.5 }} />}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {type.disciplines.map((discipline) => (
                    <Chip key={discipline} size="small" label={discipline} />
                  ))}
                </Box>
              </TableCell>
              <TableCell sx={{ fontFamily: MONOSPACE, fontSize: '0.8rem' }}>
                {type.specializes.map((base) => base.split('::').pop()).join(', ')}
              </TableCell>
              <TableCell sx={{ fontFamily: MONOSPACE, fontSize: '0.8rem' }}>
                {type.attributes.map((attribute) => attribute.name).join(', ') || '—'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>
                {type.used_by.length ? type.used_by.join(', ') : '—'}
              </TableCell>
              <TableCell padding="checkbox">
                {!type.in_library && (
                  <Tooltip
                    title={
                      !permission.allowed ? permission.reason
                        : type.used_by.length ? `Built on by ${type.used_by.join(', ')} — re-type them first`
                        : 'Remove this type'
                    }
                    describeChild
                  >
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Remove ${type.name}`}
                        disabled={!permission.allowed || type.used_by.length > 0}
                        onClick={() => setRemoving(type)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {creating && types && (
        <CreateInterfaceTypeDialog
          types={types}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            changed()
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          open
          title={`Remove ${removing.name}?`}
          message="Nothing is built on it, so nothing else changes."
          confirmLabel="Remove"
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </Stack>
  )
}
